// The hand-off between a handler='open' action button and the screen it
// opens.
//
// The values a button sets on its target are resolved from the clicked row in
// the browser, and the target is usually a DIFFERENT screen that is mounted
// fresh after a navigation (or in a new tab, from the designer's preview). The
// values must not ride the URL: a header can carry many columns, and the URL
// is the thing people copy and share. So the payload is parked here under a
// short random id, the URL carries only `?open=<id>`, and the target reads it
// back when it mounts.
//
// localStorage (not memory) so a navigation that reloads the page, or a new
// tab, still finds it; a short time-to-live so a stale id in a shared or
// bookmarked link opens the plain screen rather than replaying somebody's
// values days later. Every access is guarded — private windows and blocked
// site data throw — and an in-memory map stands in when storage is refused.

import type { OpenScreenPayload } from './types'

const PREFIX = 'twasta.open.'
const TTL_MS = 5 * 60 * 1000

interface Parked {
  at: number
  payload: OpenScreenPayload
}

const memory = new Map<string, Parked>()

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null
  } catch {
    return null
  }
}

function newId(): string {
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto
    if (c?.randomUUID) return c.randomUUID().replace(/-/g, '').slice(0, 16)
  } catch { /* fall through */ }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
}

function prune(store: Storage | null, now: number): void {
  for (const [id, entry] of memory) {
    if (now - entry.at > TTL_MS) memory.delete(id)
  }
  if (!store) return
  try {
    const stale: string[] = []
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i)
      if (!key || !key.startsWith(PREFIX)) continue
      try {
        const entry = JSON.parse(store.getItem(key) || 'null') as Parked | null
        if (!entry || now - Number(entry.at || 0) > TTL_MS) stale.push(key)
      } catch {
        stale.push(key)
      }
    }
    stale.forEach((key) => { try { store.removeItem(key) } catch { /* ignore */ } })
  } catch { /* storage enumeration refused — the TTL check on read still holds */ }
}

/** Park a payload; returns the id the target's URL carries. */
export function stashOpen(payload: OpenScreenPayload): string {
  const id = newId()
  const now = Date.now()
  const entry: Parked = { at: now, payload }
  memory.set(id, entry)
  const store = storage()
  prune(store, now)
  try {
    store?.setItem(PREFIX + id, JSON.stringify(entry))
  } catch { /* quota / blocked — the in-memory copy serves same-tab opens */ }
  return id
}

/**
 * Read a parked payload, or null when it is unknown or expired. Reading does
 * not consume it: a screen may mount twice (React's development double-mount,
 * a remount after the model loads), and each mount must see the same values.
 * The target guards against running the sequence twice itself, and the entry
 * expires on its own.
 */
export function readOpen(id: string | null | undefined): OpenScreenPayload | null {
  const key = String(id || '').trim()
  if (!key) return null
  const now = Date.now()
  const local = memory.get(key)
  if (local && now - local.at <= TTL_MS) return local.payload
  const store = storage()
  if (!store) return null
  try {
    const entry = JSON.parse(store.getItem(PREFIX + key) || 'null') as Parked | null
    if (!entry || now - Number(entry.at || 0) > TTL_MS) return null
    return entry.payload || null
  } catch {
    return null
  }
}

/** Forget a payload once its screen has applied it. */
export function dropOpen(id: string | null | undefined): void {
  const key = String(id || '').trim()
  if (!key) return
  memory.delete(key)
  try { storage()?.removeItem(PREFIX + key) } catch { /* ignore */ }
}
